import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import {
  FileX,
  PhoneMissed,
  BarChart,
  Users,
  Clock,
  Truck,
  Activity,
  Map,
  CheckCircle,
  FileText,
  Siren,
  Building2,
  LayoutDashboard,
  ChevronDown,
  MapPin,
  ArrowRight
} from "lucide-react";

import "../../Styling/Landing_Pages_css/Truck.css";
import ChatbotWidget from "../ChatComponent";
import { motion, AnimatePresence } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function TruckLandingPage() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [navVisible, setNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const wrapperRef = useRef(null);

  // Refs for specific animation sections
  const howItWorksRef = useRef(null);
  const howItWorksTrackRef = useRef(null);
  const personasRef = useRef(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const slides = [
    {
      image: "/images/Landing_page_images/Truck_dispatch/truck_1.png",
      heading: "Automate Dispatch. Control Transport Execution.",
      subheading:
        "A transport execution system that uses real-time fleet visibility to assign the right truck to every load — without spreadsheets or constant calls.",
    },
    {
      image: "/images/Landing_page_images/Truck_dispatch/truck_2.png",
      heading: "Assign the Right Truck Instantly.",
      subheading:
        "When a load is planned, the system evaluates live truck location, capacity, availability, and distance to pickup, then auto-assigns the best option.",
    },
    {
      image: "/images/Landing_page_images/Truck_dispatch/truck_3.png",
      heading: "Real-Time Fleet Visibility. No Surprises.",
      subheading:
        "Track every shipment live, monitor ETAs, and get alerts for late pickups, delays, or missing PODs.",
    },
  ];

  // Auto-slide effect for hero
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Navbar scroll behavior - show on scroll up, hide on scroll down
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 100) {
        // Always show near top
        setNavVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling UP - show navbar
        setNavVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling DOWN - hide navbar
        setNavVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {

      // 1. Hero Exit Transition & Cinematic Text
      gsap.to(".hero-section-wrapper", {
        scale: 0.95,
        opacity: 0,
        filter: "blur(5px)",
        scrollTrigger: {
          trigger: ".hero-section-wrapper",
          start: "bottom bottom",
          end: "bottom top",
          scrub: 2,
          snap: {
            snapTo: 1,
            duration: { min: 0.1, max: 0.4 },
            delay: 0,
            ease: "power1.inOut"
          },
          fastScrollEnd: true,
          preventOverlapping: true
        }
      });

      // 2. Problems: Cinematic Reveal (Fixed)
      // Ensure elements are visible if JS fails, or handle via GSAP set
      // Using fromTo prevents logic issues with React Strict Mode
      const problemTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".problems-section-trigger",
          start: "top 75%", // Trigger slightly earlier
          toggleActions: "play none none reverse"
        }
      });

      // Title Lines
      const titleLines = gsap.utils.toArray(".problem-title-line");
      if (titleLines.length) {
        problemTimeline.fromTo(titleLines,
          { y: 100, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, stagger: 0.2, ease: "power4.out" }
        );
      }

      // Red Underline
      const underline = document.querySelector(".problem-underline");
      if (underline) {
        problemTimeline.fromTo(underline,
          { scaleX: 0 },
          { scaleX: 1, duration: 0.8, ease: "power3.out" },
          "-=0.5"
        );
      }

      // Subtitle
      const subtitle = document.querySelector(".problem-subtitle");
      if (subtitle) {
        problemTimeline.fromTo(subtitle,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
          "-=0.6"
        );
      }

      // Cards (Cinematic Stagger)
      // Use batch or simple stagger with fromTo
      const cards = gsap.utils.toArray(".problem-card-anim");
      if (cards.length) {
        problemTimeline.fromTo(cards,
          { y: 60, opacity: 0, scale: 0.95 },
          { y: 0, opacity: 1, scale: 1, duration: 0.8, stagger: 0.1, ease: "power3.out" },
          "-=0.4"
        );
      }

      // 3. "Retreat & Blur" Transition - Problems to Evolution
      ScrollTrigger.create({
        trigger: ".problems-section-trigger",
        start: "bottom bottom",
        end: "+=100%",
        pin: true,
        pinSpacing: false,
        scrub: 2,
        anticipatePin: 1,
        snap: {
          snapTo: 1,
          duration: { min: 0.1, max: 0.4 },
          delay: 0,
          ease: "power1.inOut"
        },
        fastScrollEnd: true,
        preventOverlapping: true,
        animation: gsap.to(".problems-section-trigger", {
          scale: 0.9,
          opacity: 0.2,
          filter: "blur(20px)",
          borderRadius: "50px",
          y: -50,
          ease: "power2.inOut"
        })
      });

      const replaceItems = gsap.utils.toArray(".replace-item");
      replaceItems.forEach((item) => {
        const arrow = item.querySelector(".replace-arrow");
        const newText = item.querySelector(".replace-new");
        const oldText = item.querySelector(".replace-old-text"); // Add class trigger

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 85%",
          }
        });

        tl.fromTo(item,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: 0.5 }
        )
          .fromTo(oldText,
            { x: -100, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" },
            "-=0.3"
          )
          .fromTo(arrow,
            { scale: 0, rotation: -180 },
            { scale: 1, rotation: 0, duration: 0.5 },
            "-=0.4"
          )
          .fromTo(newText,
            { x: 100, opacity: 0, filter: "blur(10px)" },
            { x: 0, opacity: 1, filter: "blur(0px)", duration: 0.6, ease: "power2.out" },
            "-=0.4"
          );
      });

      // Liquid Water Fill for Evolution Section
      gsap.to(".evolution-liquid-fill", {
        scaleY: 1,
        ease: "none",
        scrollTrigger: {
          trigger: ".evolution-section",
          start: "top bottom",
          end: "center center",
          scrub: true
        }
      });


      // 4. How It Works: Horizontal Scroll + Progress Line + Staggered Content
      const track = howItWorksTrackRef.current;
      const getScrollAmount = () => {
        let trackWidth = track.scrollWidth;
        return -(trackWidth - window.innerWidth);
      };

      const scrollTween = gsap.to(track, {
        x: getScrollAmount,
        ease: "none",
        scrollTrigger: {
          trigger: howItWorksRef.current,
          pin: true,
          scrub: 2,
          start: "top top",
          end: () => `+=${track.scrollWidth - window.innerWidth}`,
          invalidateOnRefresh: true,
          anticipatePin: 1,
          id: "how-scroll",
          snap: {
            snapTo: 1,
            duration: { min: 0.2, max: 0.8 },
            delay: 0,
            ease: "power1.inOut"
          },
          fastScrollEnd: true
        }
      });

      // 8. Scroll-Scrubbed Progress Line
      gsap.fromTo(".how-progress-bar",
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: "none",
          scrollTrigger: {
            trigger: howItWorksRef.current,
            scrub: 1,
            start: "top top",
            end: () => `+=${track.scrollWidth - window.innerWidth}`,
          }
        }
      );

      // 9. Icon + Text Pair Stagger (Linked to Horizontal Scroll)
      const stepCards = gsap.utils.toArray(".how-step-card");
      stepCards.forEach((card, i) => {
        const icon = card.querySelector(".how-icon-target");
        const text = card.querySelector(".how-text-target");

        // We use the index to estimate when it enters view in the horizontal scroll
        // Or simpler: trigger when the card itself enters the viewport horizontally?
        // GSAP containerAnimation is perfect for this.

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            containerAnimation: scrollTween,
            start: "left 80%", // Trigger earlier so last cards can fully animate
            toggleActions: "play reverse play reverse"
          }
        });

        tl.fromTo(icon,
          { scale: 0, rotation: -45, opacity: 0 },
          { scale: 1, rotation: 0, opacity: 1, duration: 0.5, ease: "back.out(1.7)" }
        )
          .fromTo(text,
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: "power2.out" },
            "-=0.3"
          );
      });


      // 5. Control Tower: Text Fill & "Constructed" Interface Entrance
      gsap.fromTo(".control-tower-text",
        { backgroundSize: "0% 100%" },
        {
          backgroundSize: "100% 100%",
          ease: "none",
          scrollTrigger: {
            trigger: ".control-tower-text",
            start: "top 80%",
            end: "bottom 50%",
            scrub: true
          }
        }
      );

      // Stats: "Real Software Assembling" - Fly in from different directions
      const stats = gsap.utils.toArray(".stat-card-anim");
      stats.forEach((stat, i) => {
        // Alternating directions based on index
        const xOffset = i % 2 === 0 ? -100 : 100;
        const yOffset = i < 2 ? -100 : 100;

        gsap.fromTo(stat,
          { opacity: 0, x: xOffset, y: yOffset },
          {
            opacity: 1, x: 0, y: 0,
            duration: 0.8,
            ease: "back.out(1.2)",
            scrollTrigger: {
              trigger: ".stats-grid-trigger",
              start: "top 75%"
            }
          }
        );
      });

      // 5b. Control Tower -> Personas "Deck Slide" Transition
      // Pin Control Tower so Personas slides OVER it
      ScrollTrigger.create({
        trigger: ".control-tower-trigger",
        start: "bottom bottom",
        end: "+=100%",
        pin: true,
        pinSpacing: false,
        scrub: true,
        snap: {
          snapTo: 1,
          duration: { min: 0.1, max: 0.4 },
          delay: 0,
          ease: "power1.inOut"
        },
        fastScrollEnd: true,
        animation: gsap.to(".control-tower-trigger", {
          scale: 0.9,
          opacity: 0.2,
          filter: "blur(20px)",
          borderRadius: "50px",
          y: -50,
          ease: "none"
        })
      });

      gsap.to(".siren-glow", {
        boxShadow: "0 0 60px 20px rgba(239, 68, 68, 0.4)",
        repeat: -1,
        yoyo: true,
        duration: 0.8
      });


      // 6. Personas: Stacked Panels Animation with UNIQUE Entrances
      const personaPanels = gsap.utils.toArray(".persona-panel");
      personaPanels.forEach((panel, i) => {
        // Get the content card inside each panel
        const contentCard = panel.querySelector(".persona-content-card");

        // Define unique entrance animations based on index
        let fromVars = {};
        if (i === 0) {
          // First card: Slide from LEFT
          fromVars = { x: "-100%", opacity: 0, rotation: -5 };
        } else if (i === 1) {
          // Second card: Fade in with SCALE from below
          fromVars = { y: 100, opacity: 0, scale: 0.8 };
        } else {
          // Third card: Slide from RIGHT
          fromVars = { x: "100%", opacity: 0, rotation: 5 };
        }

        // Animate content card entrance
        gsap.fromTo(contentCard,
          fromVars,
          {
            x: 0,
            y: 0,
            opacity: 1,
            scale: 1,
            rotation: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: panel,
              start: "top 80%",
              end: "top 30%",
              scrub: 1
            }
          }
        );

        ScrollTrigger.create({
          trigger: panel,
          start: "top top",
          end: "bottom top",
          pin: true,
          pinSpacing: false,
          scrub: 2,
          anticipatePin: 1,
          snap: {
            snapTo: 1,
            duration: { min: 0.1, max: 0.4 },
            delay: 0,
            ease: "power1.inOut"
          },
          fastScrollEnd: true
        });

        // Smoothly fade out previous panel as next one covers it
        if (i < personaPanels.length - 1) {
          gsap.fromTo(panel,
            { opacity: 1, scale: 1, filter: "brightness(1) blur(0px)" },
            {
              opacity: 0,
              scale: 0.95,
              filter: "brightness(0.5) blur(10px)",
              ease: "power2.inOut",
              scrollTrigger: {
                trigger: personaPanels[i + 1],
                start: "top bottom",
                end: "top top",
                scrub: 2
              }
            });
        }
      });

      // 6b. FAQ Section: Horizontal Slide-In Transition
      gsap.fromTo(".faq-section-trigger",
        { x: "-30%", opacity: 0 },
        {
          x: "0%",
          opacity: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".faq-section-trigger",
            start: "top bottom",
            end: "top 40%",
            scrub: 2
          }
        }
      );

      gsap.fromTo(".faq-content-wrapper",
        { x: "30%", opacity: 0 },
        {
          x: "0%",
          opacity: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".faq-section-trigger",
            start: "top bottom",
            end: "top 40%",
            scrub: 2
          }
        }
      );

      // 7. CTA: Text Fill Reveal & "Water Cover" Transition
      gsap.fromTo(".cta-fill-text",
        { backgroundSize: "0% 100%" },
        {
          backgroundSize: "100% 100%",
          duration: 1.5,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: ".cta-section",
            start: "top 60%",
            end: "bottom 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Parallax Cover Effect: FAQ moves slower, CTA covers it
      ScrollTrigger.create({
        trigger: ".faq-section-trigger",
        start: "bottom bottom",
        end: "+=100%", // Extend scroll distance
        pin: true,     // Pin the FAQ
        pinSpacing: false, // Let the next section (CTA) scroll OVER it
        snap: {
          snapTo: [0, 1],
          duration: { min: 0.3, max: 0.8 },
          delay: 0,
          ease: "power1.inOut"
        }
      });

      // Since CTA is z-20 and FAQ is z-10 in JSX, CTA naturally covers.
      // We just added pinSpacing:false so CTA continues scrolling UP while FAQ sits still.


      gsap.from(".cta-content", {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".cta-section",
          start: "top 60%"
        }
      });

      // 7. Extra Snapping for Seamless Flow
      ScrollTrigger.create({
        trigger: ".evolution-section",
        start: "top top",
        end: "bottom top",
        snap: {
          snapTo: [0, 1],
          duration: { min: 0.2, max: 0.6 },
          delay: 0,
          ease: "power1.inOut"
        }
      });

      ScrollTrigger.create({
        trigger: ".cta-section",
        start: "top bottom",
        end: "top top",
        snap: {
          snapTo: [0, 1],
          duration: { min: 0.2, max: 0.6 },
          delay: 0,
          ease: "power1.inOut"
        }
      });

    }, wrapperRef);

    // Global Scroll Configuration for "Aggressive" snappy feel
    ScrollTrigger.normalizeScroll(true);
    ScrollTrigger.config({
      ignoreMobileResize: true,
      autoRefreshEvents: "visibilitychange,DOMContentLoaded,load,resize"
    });

    // Ensure ScrollTrigger refreshes after all elements are measured
    ScrollTrigger.refresh();

    return () => {
      ctx.revert();
      ScrollTrigger.normalizeScroll(false);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="bg-black text-slate-100 selection:bg-cyan-500 selection:text-white overflow-x-hidden font-sans">
      {/* 🔷 CUSTOM NAVBAR */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ease-in-out ${navVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
        <div className="w-full backdrop-blur-xl bg-slate-900/80 border-b border-white/10 px-4 md:px-8 lg:px-12 py-3 flex items-center justify-between shadow-2xl">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-black text-lg shadow-lg group-hover:scale-110 transition-transform">
              D
            </div>
            <span className="text-white font-bold text-lg hidden md:block">Digital Dream Works</span>
          </a>

          {/* Nav Links */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="/Labs" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium px-3 py-2">Labs</a>
            <a href="/How_we_work" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium px-3 py-2">How We Work</a>
            <a href="/PartnerShips" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium px-3 py-2">Partnerships</a>
            <a href="/AboutUs" className="text-slate-300 hover:text-cyan-400 transition-colors font-medium px-3 py-2">About</a>
          </div>

          {/* CTA Button */}
          <a
            href="https://studio.digitaldreamworksagency.com/access"
            className="inline-flex items-center justify-center h-10 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold text-sm hover:shadow-[0_5px_30px_rgba(34,211,238,0.4)] hover:scale-105 transition-all whitespace-nowrap"
          >
            Get Demo
          </a>
        </div>
      </nav>

      {/* 🟢 1. HERO - Custom Cinematic Hero */}
      <section className="hero-section-wrapper relative h-screen w-full z-10 overflow-hidden flex items-center justify-center">
        {/* Background Slides with Crossfade */}
        {slides.map((slide, i) => (
          <div
            key={i}
            className={`hero-bg-slide absolute inset-0 z-0 transition-opacity duration-1000 ease-in-out ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img
              src={slide.image}
              alt={slide.heading}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
          </div>
        ))}

        {/* Animated Grid Overlay */}
        <div className="absolute inset-0 z-0 opacity-10 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)", backgroundSize: "80px 80px" }}></div>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          {/* Badge */}
          <div className="hero-badge inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-10 backdrop-blur-md">
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
            <span className="text-cyan-400 font-bold text-sm tracking-widest uppercase">Truck Dispatching Operations System</span>
          </div>

          {/* Heading */}
          <h1 className="hero-heading text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-[1.1] tracking-tight">
            {slides[currentSlide].heading.split(' ').map((word, wi) => (
              <span key={wi} className="inline-block mr-4 hero-word" style={{ animationDelay: `${wi * 0.1}s` }}>
                {word}
              </span>
            ))}
          </h1>

          {/* Subheading */}
          <p className="hero-subheading text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-12 leading-relaxed font-light">
            {slides[currentSlide].subheading}
          </p>

          {/* CTA Buttons */}
          <div className="hero-cta flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://studio.digitaldreamworksagency.com/access"
              className="inline-flex items-center justify-center h-14 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-bold text-base hover:scale-105 hover:shadow-[0_10px_40px_rgba(34,211,238,0.3)] transition-all duration-300 gap-2"
            >
              Request Demo Access <ArrowRight size={18} />
            </a>
            <a
              href="https://studio.digitaldreamworksagency.com/"
              className="inline-flex items-center justify-center h-14 px-8 border-2 border-slate-500 text-white rounded-xl font-bold text-base hover:bg-white/10 hover:border-cyan-500 transition-all duration-300"
            >
              Explore DDW Studio
            </a>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-3 mt-16">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${i === currentSlide ? 'bg-cyan-400 w-8' : 'bg-slate-600 hover:bg-slate-500'}`}
              />
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-slate-500 text-xs tracking-widest uppercase">Scroll</span>
          <ChevronDown size={24} className="text-cyan-400" />
        </div>
      </section>

      {/* 🔴 2. PROBLEMS - Cinematic Sequence & Chaos Background */}
      <section className="problems-section-trigger min-h-screen flex items-center justify-center py-24 px-6 relative z-20 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black overflow-hidden">
        {/* Animated Chaos Background */}
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1625014618427-fbc980b974f5?q=80&w=2000" alt="Chaos Background" className="w-full h-full object-cover opacity-20 mix-blend-overlay" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950"></div>
        </div>
        <div className="absolute inset-0 z-0 opacity-50 pointer-events-none">
          {/* Existing Blobs */}
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-red-900 filter blur-[100px] animate-blob"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-orange-900 filter blur-[100px] animate-blob animation-delay-2000"></div>
          <div className="absolute top-[20%] right-[20%] w-[20%] h-[20%] rounded-full bg-red-800 filter blur-[80px] animate-blob animation-delay-4000"></div>

          {/* NEW: Extra Graphics */}
          <div className="absolute bottom-[20%] left-[20%] w-[15%] h-[15%] rounded-full bg-purple-900/40 filter blur-[60px] animate-blob animation-delay-3000"></div>
          <div className="absolute top-[40%] left-[50%] w-[10%] h-[10%] rounded-full bg-rose-800/30 filter blur-[40px] animate-pulse"></div>
        </div>

        {/* NEW: Subtle Grid Overlay */}
        <div className="absolute inset-0 z-0 opacity-5 pointer-events-none" style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "40px 40px" }}></div>


        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="text-center mb-24">
            <h2 className="text-sm font-bold text-red-500 tracking-[0.3em] uppercase mb-4">The Bottleneck</h2>

            <div className="mb-8">
              <div className="overflow-hidden">
                <h3 className="problem-title-line text-5xl md:text-8xl font-black text-white tracking-tight leading-[1.1]">
                  Manual Dispatch
                </h3>
              </div>
              <div className="overflow-hidden">
                <h3 className="problem-title-line text-5xl md:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-slate-400 tracking-tight leading-[1.1]">
                  Breaks at Scale
                </h3>
              </div>
            </div>

            <div className="problem-underline w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto rounded-full mb-8 shadow-[0_0_15px_red] origin-center"></div>

            <p className="problem-subtitle text-slate-400 text-2xl font-light max-w-3xl mx-auto leading-relaxed">
              Your team can handle 5 trucks. But at 20, the spreadsheets break and the chaos begins.
            </p>
          </div>

          <div className="problems-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: "Chaos Communication", desc: "Loads assigned via WhatsApp & phone. Information is lost instantly." },
              { icon: PhoneMissed, title: "Availability Check", desc: "Calling drivers one-by-one to just to find who is empty." },
              { icon: Map, title: "Blind Dispatch", desc: "No visibility on which truck is actually closest. Miles wasted." },
              { icon: Clock, title: "Hidden Delays", desc: "Late pickups discovered only after the customer calls you." },
              { icon: FileX, title: "Missing PODs", desc: "Chasing paperwork manually before accounting can invoice." },
              { icon: Activity, title: "Single Point of Failure", desc: "Operations depend entirely on one dispatcher's memory." }
            ].map((p, i) => (
              <div key={i} className="problem-card-anim group p-10 rounded-[2.5rem] bg-slate-900/40 border border-slate-800/60 hover:border-red-500/50 hover:bg-slate-900/80 hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_40px_-10px_rgba(239,68,68,0.15)] transition-all duration-500 backdrop-blur-sm shadow-lg">
                <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:bg-red-500 group-hover:text-white transition-all duration-500 shadow-lg">
                  <p.icon size={32} />
                </div>
                <h4 className="text-2xl font-bold text-slate-100 mb-4 group-hover:text-red-400 transition-colors">{p.title}</h4>
                <p className="text-slate-400 text-lg leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🟠 3. REPLACES - Before -> After Transition & Tech Background */}
      {/* 🟠 3. REPLACES - Before -> After Transition & Tech Background */}
      <section className="min-h-screen flex items-center justify-center py-32 px-6 relative z-30 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden evolution-section shadow-[0_-50px_100px_rgba(0,0,0,0.9)]">
        {/* Liquid Water Fill Transition */}
        <div className="evolution-liquid-fill absolute inset-0 z-0 bg-gradient-to-t from-cyan-900/40 via-blue-900/20 to-transparent origin-bottom scale-y-0"></div>

        {/* STOCK IMG BACKGROUND */}
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2000" alt="Global Network" className="w-full h-full object-cover opacity-60 mix-blend-screen" />
          <div className="absolute inset-0 bg-slate-950/80"></div>
        </div>

        {/* Tech Grid Background */}
        <div className="absolute inset-0 z-0 opacity-10" style={{
          backgroundImage: "linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)",
          backgroundSize: "50px 50px"
        }}></div>
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950"></div>

        {/* NEW: Floating Tech Nodes */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-10 w-4 h-4 bg-cyan-500 rounded-full shadow-[0_0_15px_cyan] animate-ping opacity-20"></div>
          <div className="absolute bottom-1/3 right-20 w-3 h-3 bg-blue-500 rounded-full shadow-[0_0_10px_blue] animate-pulse opacity-30"></div>
          <div className="absolute top-20 right-1/4 w-32 h-32 border border-cyan-500/10 rounded-full animate-spin-slow"></div>
          <div className="absolute bottom-10 left-1/3 w-64 h-64 border border-blue-500/5 rounded-full animate-reverse-spin"></div>
        </div>

        <div className="max-w-6xl mx-auto w-full relative z-10 flex flex-col items-center">
          <div className="mb-24 text-center">
            <h2 className="text-5xl md:text-8xl font-black mb-8 leading-tight">
              Evolution of <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Operations</span>
            </h2>
            <p className="text-slate-400 text-2xl font-light">Shift from reactive panic to predictive control.</p>
          </div>

          <div className="space-y-6">
            {[
              { old: "Phone call coordination", new: "Automated dispatch assignment" },
              { old: "Manual route planning", new: "Intelligent fleet optimization" },
              { old: "Back-and-forth rescheduling", new: "Real-time coordination engine" },
              { old: "Reactive decision making", new: "Predictive load balancing" }
            ].map((item, i) => (
              <div key={i} className="replace-item relative grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden rounded-[2rem] border border-slate-700 bg-slate-900 shadow-2xl group">
                {/* Old Side */}
                <div className="relative p-10 flex items-center justify-end border-b md:border-b-0 md:border-r border-slate-700 bg-slate-950/50 group-hover:bg-red-950/10 transition-colors duration-500">
                  <div className="replace-old-text text-slate-500 font-medium text-xl md:text-2xl text-right line-through decoration-red-500/40 decoration-2 transition-all group-hover:text-red-400/60">
                    {item.old}
                  </div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center border border-slate-600 group-hover:border-cyan-500 transition-colors">
                    <div className="replace-arrow text-slate-400 group-hover:text-cyan-400"><ArrowRight size={20} /></div>
                  </div>
                </div>

                {/* New Side */}
                <div className="relative p-10 flex items-center bg-gradient-to-r from-slate-900 to-slate-800 group-hover:from-cyan-950/20 group-hover:to-blue-950/20 transition-colors duration-500">
                  <div className="replace-new text-white font-bold text-2xl md:text-3xl pl-8 tracking-tight group-hover:text-cyan-400 transition-colors">
                    {item.new}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🔵 4. HOW IT WORKS - Horizontal Scroll Section (Long Height Feel) */}
      {/* 🔵 4. HOW IT WORKS - Horizontal Scroll Section (Long Height Feel) */}
      <section ref={howItWorksRef} className="relative h-screen bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-950/20 via-slate-950 to-black overflow-hidden flex flex-col z-30">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?q=80&w=2000" alt="Highway Night" className="w-full h-full object-cover opacity-40 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-slate-950/80"></div>
        </div>


        {/* Progress Line */}
        <div className="absolute bottom-10 left-10 right-10 h-1 bg-slate-800 rounded-full z-40 overflow-hidden">
          <div className="how-progress-bar h-full w-full bg-cyan-500 origin-left scale-x-0"></div>
        </div>

        <div ref={howItWorksTrackRef} className="flex h-full items-center pl-[10vw] pr-[10vw]">
          {[
            { step: "01", icon: BarChart, title: "Load Intake", desc: "Load enters system via API, EDI, or manual entry. All details captured automatically.", img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=1000" },
            { step: "02", icon: Activity, title: "Evaluation", desc: "System evaluates fleet location, HOS, capacity, and route preferences instantly.", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1000" },
            { step: "03", icon: CheckCircle, title: "Assignment", desc: "Best-fit truck is recommended or auto-assigned. Dispatch sent to driver app.", img: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=1000" },
            { step: "04", icon: MapPin, title: "Execution", desc: "Live GPS tracking, geofence alerts, and eta updates during transport.", img: "https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?q=80&w=1000" },
            { step: "05", icon: FileText, title: "Completion", desc: "Driver uploads POD instantly. Load marked delivered and ready for invoice.", img: "https://images.unsplash.com/photo-1617957770621-077a05584523?q=80&w=1000" }
          ].map((s, i) => (
            <div key={i} className="how-step-card relative flex-shrink-0 w-[80vw] md:w-[600px] h-[60vh] mx-6 p-10 md:p-16 rounded-[3rem] bg-slate-900 border border-slate-700/50 flex flex-col justify-center transform shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 z-0 opacity-40 mix-blend-overlay transition-opacity duration-500 group-hover:opacity-20">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent z-0"></div>

              <div className="absolute -top-10 -right-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl group-hover:bg-cyan-400/30 transition-colors duration-500"></div>

              <div className="how-icon-target w-24 h-24 rounded-3xl bg-slate-800 border border-slate-700 text-cyan-400 flex items-center justify-center mb-10 shadow-lg relative z-10">
                <s.icon size={48} />
              </div>

              <span className="text-9xl font-black text-slate-800 absolute top-6 right-8 opacity-50 z-0 select-none">{s.step}</span>

              <div className="how-text-target relative z-10">
                <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">{s.title}</h3>
                <p className="text-xl text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 🔴 5. CONTROL TOWER - Text Fill Reveal & Fake UI */}
      <section className="control-tower-trigger py-40 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-red-900/10 via-black to-black relative overflow-hidden z-20 h-screen flex flex-col justify-center">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2000" alt="Radar Tech" className="w-full h-full object-cover opacity-25 mix-blend-hard-light" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-red-900/10 via-black to-black"></div>

        {/* NEW: Radar Sweep & Pulsing Rings */}
        <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center opacity-20">
          <div className="w-[80vw] h-[80vw] md:w-[600px] md:h-[600px] border border-red-500/20 rounded-full absolute animate-ping-slow"></div>
          <div className="w-[60vw] h-[60vw] md:w-[400px] md:h-[400px] border border-red-500/30 rounded-full absolute"></div>
          <div className="w-[80vw] h-[80vw] md:w-[600px] md:h-[600px] absolute bg-gradient-to-t from-transparent via-red-500/5 to-transparent animate-spin-slow rounded-full opacity-30"></div>
        </div>


        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <div className="siren-glow inline-flex p-6 rounded-full bg-red-600/10 border border-red-500/30 mb-12">
            <Siren size={64} className="text-red-500" />
          </div>

          <h2 className="control-tower-text text-5xl md:text-8xl font-black uppercase text-transparent bg-clip-text bg-gradient-to-r from-slate-500 to-slate-300 bg-no-repeat mb-12"
            style={{ backgroundImage: "linear-gradient(to right, #ef4444, #f97316)" }}>
            Know What’s Late <br /> Before Your Customer Does
          </h2>

          <p className="text-slate-400 text-2xl font-light max-w-3xl mx-auto mb-20">
            Late pickups, missing PODs, and at-risk loads are flagged in real time.
            Act before revenue is affected.
          </p>

          {/* Fake UI Stats */}
          <div className="stats-grid-trigger grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { l: "DELAY ALERTS", v: "INSTANT", c: "text-red-500" },
              { l: "FLEET UPTIME", v: "99.9%", c: "text-green-500" },
              { l: "MANUAL CALLS", v: "0", c: "text-slate-100" },
              { l: "ROI / MONTH", v: "15%", c: "text-cyan-500" }
            ].map((stat, i) => (
              <div key={i} className="stat-card-anim p-8 rounded-3xl bg-slate-900/50 border border-slate-800 backdrop-blur-md">
                <div className={`text-4xl font-bold ${stat.c} mb-2`}>{stat.v}</div>
                <div className="text-xs font-bold text-slate-600 tracking-widest">{stat.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🟡 6. PERSONAS - Stacked Panels Animation */}
      <div ref={personasRef} className="personas-wrapper relative z-30 font-sans shadow-[0_-50px_100px_rgba(0,0,0,0.9)]">
        {/* Built For Scale Header with Background */}
        <div className="relative py-32 text-center px-6 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2000" alt="Truck Fleet" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black"></div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <h2 className="text-5xl md:text-8xl font-black text-white mb-6 drop-shadow-2xl">Built For Scale</h2>
            <p className="text-slate-300 text-xl md:text-2xl font-light max-w-3xl mx-auto">Engineered for high-volume logistics operations.</p>
          </div>
        </div>

        {[
          {
            title: "Trucking Companies",
            desc: "20–200+ trucks. Optimize fleet utilization, reduce deadhead, and automate driver assignment.",
            icon: Truck,
            bg: "bg-slate-900"
          },
          {
            title: "3PLs & Freight Brokers",
            desc: "Manage complex logistics networks. Real-time carrier tracking and automated document collection.",
            icon: Building2,
            bg: "bg-slate-800"
          },
          {
            title: "Dispatch Teams",
            desc: "Stop doing data entry. Focus on high-value carrier relationships and exception management.",
            icon: LayoutDashboard,
            bg: "bg-slate-950"
          }
        ].map((item, i) => (
          <section key={i} className={`persona-panel h-screen w-full sticky top-0 flex items-center justify-center p-6 ${item.bg} overflow-hidden`}>
            <div className="absolute inset-0 z-0 opacity-20">
              {i === 0 && <img src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2000" className="w-full h-full object-cover" alt="Truck Fleet" />}
              {i === 1 && <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2000" className="w-full h-full object-cover" alt="Warehouse" />}
              {i === 2 && <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2000" className="w-full h-full object-cover" alt="Data Center" />}
            </div>

            {/* NEW: Floating Geometry Background */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-10">
              {i === 0 && <div className="absolute top-10 right-10 w-40 h-40 border-4 border-slate-500/20 rotate-12"></div>}
              {i === 1 && <div className="absolute bottom-20 left-10 w-32 h-32 bg-slate-500/10 rounded-full"></div>}
              {i === 2 && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[10px] bg-cyan-500/10 -rotate-45"></div>}
            </div>

            <div className="persona-content-card max-w-4xl w-full p-12 md:p-20 rounded-[3rem] bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl flex flex-col md:flex-row items-center gap-12 relative z-10">

              <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0 shadow-xl">
                <item.icon size={64} />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-4xl md:text-6xl font-black text-white mb-6">{item.title}</h3>
                <p className="text-slate-300 text-xl md:text-2xl leading-relaxed">{item.desc}</p>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* 🟢 7. FAQ - Horizontal Slide-In */}
      <section className="faq-section-trigger min-h-screen flex items-center justify-center py-32 px-6 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black relative z-40 overflow-hidden">

        {/* STOCK IMG BACKGROUND */}
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1557682250-33bd709cbe85?q=80&w=2000" alt="Abstract Gradient" className="w-full h-full object-cover opacity-20 mix-blend-overlay" />
        </div>

        {/* Animated Geometric Background */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          {/* Large Rotating Ring */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] md:w-[600px] md:h-[600px] border border-cyan-500/10 rounded-full animate-spin-slow"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] md:w-[450px] md:h-[450px] border border-blue-500/10 rounded-full animate-reverse-spin"></div>

          {/* Floating Orbs */}
          <div className="absolute top-[15%] right-[10%] w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-[20%] left-[5%] w-24 h-24 bg-blue-500/10 rounded-full blur-xl animate-bounce-slow"></div>
          <div className="absolute top-[60%] right-[25%] w-16 h-16 bg-purple-500/10 rounded-full blur-lg animate-pulse"></div>

          {/* Glowing Lines */}
          <div className="absolute top-0 left-1/4 w-[1px] h-full bg-gradient-to-b from-transparent via-cyan-500/20 to-transparent"></div>
          <div className="absolute top-0 right-1/4 w-[1px] h-full bg-gradient-to-b from-transparent via-blue-500/20 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="faq-content-wrapper max-w-3xl w-full relative z-10 text-center">
          {/* Subheading */}
          <p className="text-cyan-400 text-sm md:text-base font-bold tracking-[0.3em] uppercase mb-6">Got Questions?</p>

          {/* Main Heading */}
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black mb-8 text-white leading-tight">
            Common <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Questions</span>
          </h2>

          {/* Description */}
          <p className="text-slate-400 text-lg md:text-xl mb-16 max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about automating your dispatch operations.
          </p>

          {/* FAQ Cards */}
          <div className="space-y-4 text-left">
            {[
              { q: "Does this replace our TMS?", a: "No. It integrates with existing systems to handle the execution layer—automating dispatch, tracking, and driver coordination." },
              { q: "How long does onboarding take?", a: "Typically 1–2 weeks. We map your fleet, import driver data, and configure dispatch rules." },
              { q: "Is there a mobile app for drivers?", a: "Yes. Drivers access a simple, no-login required link or app to see loads and upload PODs." }
            ].map((faq, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden transition-all duration-300 hover:bg-white/10 hover:border-cyan-500/30 shadow-xl">
                <button onClick={() => toggleFAQ(i)} className="w-full p-6 md:p-8 flex items-center justify-between text-left">
                  <span className="font-semibold text-lg md:text-xl text-white">{faq.q}</span>
                  <div className={`p-2 rounded-full bg-white/10 transition-all duration-300 ${openFAQ === i ? "rotate-180 bg-cyan-500 text-black" : "text-white"}`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                <AnimatePresence>
                  {openFAQ === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="px-6 md:px-8 pb-6 md:pb-8 text-slate-300 text-base md:text-lg leading-relaxed border-t border-white/5 pt-4">{faq.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 🟣 8. CTA - Water Cover Transition */}
      <section className="cta-section relative min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-950 to-slate-950 text-center px-6 z-50 overflow-hidden shadow-[0_-50px_100px_rgba(0,0,0,0.8)]">
        {/* Water Wall Gradient Top Overlay */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-black/50 to-transparent z-10 pointer-events-none"></div>

        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1496337589254-7e19d01cec44?q=80&w=2000" alt="Warp Speed" className="w-full h-full object-cover opacity-30 mix-blend-screen" />
        </div>
        <div className="cta-content relative z-10">
          <h2 className="text-6xl md:text-9xl font-black text-white mb-10 leading-none">
            READY TO <br />
            <span className="cta-fill-text block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 bg-no-repeat"
              style={{ backgroundImage: "linear-gradient(to right, #22d3ee, #3b82f6)" }}>
              AUTOMATE?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-slate-300 mb-16 max-w-3xl mx-auto">
            Join logsitics leaders transforming their operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://studio.digitaldreamworksagency.com/access" className="inline-flex items-center justify-center h-16 px-10 bg-white text-slate-950 rounded-xl font-bold text-lg hover:scale-105 transition-all duration-300 gap-2">
              Request Demo Access <ArrowRight size={20} />
            </a>
            <a href="https://calendly.com/digi-dreamworks/onboarding-call" className="inline-flex items-center justify-center h-16 px-10 border-2 border-slate-500 text-white rounded-xl font-bold text-lg hover:bg-white/10 hover:border-white transition-all duration-300">
              Book Operational Call
            </a>
          </div>
        </div>
      </section>

      <ChatbotWidget />

      {/* 🔷 CUSTOM FOOTER */}
      <footer className="relative z-50 bg-slate-950 border-t border-slate-800">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">

            {/* Brand Column */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-black text-xl shadow-lg">
                  D
                </div>
                <span className="text-white font-bold text-xl">Digital Dream Works</span>
              </div>
              <p className="text-slate-400 leading-relaxed mb-8 max-w-md">
                An AI and Software Studio building production-ready systems for agencies, enterprises, and strategic partners. Designed for real operations. Deployed for scale.
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/digital-dream-works" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center text-slate-400 hover:text-white transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                </a>
                <a href="https://www.instagram.com/digi.dreamworks" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-cyan-500 flex items-center justify-center text-slate-400 hover:text-white transition-all">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                </a>
              </div>
            </div>

            {/* Links Column */}
            <div>
              <h4 className="text-white font-bold mb-6">Explore</h4>
              <ul className="space-y-4">
                <li><a href="/AboutUs" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"><ArrowRight size={14} /> About</a></li>
                <li><a href="/Labs" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"><ArrowRight size={14} /> Labs</a></li>
                <li><a href="/PartnerShips" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"><ArrowRight size={14} /> Partnerships</a></li>
                <li><a href="/How_we_work" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"><ArrowRight size={14} /> How We Work</a></li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-white font-bold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="text-slate-400 flex items-start gap-3">
                  <MapPin size={18} className="text-cyan-500 flex-shrink-0 mt-1" />
                  <span>Digital Dream Works LLC<br />St. Petersburg, FL, USA</span>
                </li>
                <li>
                  <a href="mailto:hello@DigitalDreamWorksAgency.com" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center">✉</span>
                    hello@DigitalDreamWorksAgency.com
                  </a>
                </li>
                <li>
                  <a href="tel:+17867352988" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-3">
                    <span className="w-5 h-5 flex items-center justify-center">📞</span>
                    +1 786 735 2988
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © 2026 Digital Dream Works. All rights reserved.
            </p>
            <p className="text-slate-600 text-xs text-center md:text-right">
              Building operational AI systems for regulated industries.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
